import { useState } from "react";
import {
  Card,
  Form,
  Input,
  Switch,
  Button,
  Typography,
  Space,
  Divider,
  Select,
  message,
  Tabs,
} from "antd";
import {
  SettingOutlined,
  UserOutlined,
  BellOutlined,
  EyeOutlined,
  DatabaseOutlined,
  CodeOutlined,
} from "@ant-design/icons";
import PromptsManager from "./PromptsManager";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface ConfigurationProps {}

const Configuration: React.FC<ConfigurationProps> = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      // Simular guardado de configuración
      await new Promise((resolve) => setTimeout(resolve, 1000));
      message.success("Configuración guardada exitosamente");
    } catch (error) {
      message.error("Error al guardar la configuración");
    } finally {
      setLoading(false);
    }
  };

  const tabItems = [
    {
      key: "general",
      label: (
        <span>
          <SettingOutlined />
          General
        </span>
      ),
      children: (
        <ConfigurationContent
          form={form}
          loading={loading}
          handleSave={handleSave}
        />
      ),
    },
    {
      key: "prompts",
      label: (
        <span>
          <CodeOutlined />
          Prompts
        </span>
      ),
      children: <PromptsManager />,
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ marginBottom: "24px" }}>
        <Title level={3}>
          <SettingOutlined style={{ marginRight: "8px" }} />
          Configuración
        </Title>
        <Text type="secondary">
          Personaliza la aplicación según tus preferencias y necesidades
        </Text>
      </div>

      <Tabs defaultActiveKey="general" items={tabItems} />
    </div>
  );
};

const ConfigurationContent: React.FC<{
  form: any;
  loading: boolean;
  handleSave: (values: any) => void;
}> = ({ form, loading, handleSave }) => {
  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSave}
      initialValues={{
        defaultPrompt: "",
        autoSave: true,
        notifications: true,
        theme: "light",
        language: "es",
        maxFileSize: 10,
      }}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Configuración de Usuario */}
        <Card
          title={
            <Space>
              <UserOutlined />
              <span>Perfil de Usuario</span>
            </Space>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Form.Item
              name="displayName"
              label="Nombre para mostrar"
              extra="Este nombre aparecerá en la interfaz"
            >
              <Input placeholder="Tu nombre completo" />
            </Form.Item>

            <Form.Item
              name="defaultPrompt"
              label="Prompt por defecto"
              extra="Prompt que se utilizará automáticamente al procesar imágenes"
            >
              <TextArea
                rows={4}
                placeholder="Escribe aquí el prompt por defecto para el análisis de imágenes..."
              />
            </Form.Item>
          </Space>
        </Card>

        {/* Configuración de Aplicación */}
        <Card
          title={
            <Space>
              <EyeOutlined />
              <span>Preferencias de Interfaz</span>
            </Space>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Form.Item
              name="theme"
              label="Tema"
              extra="Selecciona el tema visual de la aplicación"
            >
              <Select placeholder="Selecciona un tema">
                <Option value="light">Claro</Option>
                <Option value="dark">Oscuro</Option>
                <Option value="auto">Automático</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="language"
              label="Idioma"
              extra="Idioma de la interfaz de usuario"
            >
              <Select placeholder="Selecciona un idioma">
                <Option value="es">Español</Option>
                <Option value="en">English</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="autoSave"
              label="Guardado automático"
              valuePropName="checked"
              extra="Guarda automáticamente los cambios mientras trabajas"
            >
              <Switch />
            </Form.Item>
          </Space>
        </Card>

        {/* Configuración de Procesamiento */}
        <Card
          title={
            <Space>
              <DatabaseOutlined />
              <span>Configuración de Procesamiento</span>
            </Space>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Form.Item
              name="maxFileSize"
              label="Tamaño máximo de archivo (MB)"
              extra="Tamaño máximo permitido para las imágenes"
            >
              <Select placeholder="Selecciona el tamaño máximo">
                <Option value={5}>5 MB</Option>
                <Option value={10}>10 MB</Option>
                <Option value={20}>20 MB</Option>
                <Option value={50}>50 MB</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="retryAttempts"
              label="Intentos de reintento"
              extra="Número de intentos si falla el procesamiento"
            >
              <Select placeholder="Selecciona el número de intentos">
                <Option value={1}>1 intento</Option>
                <Option value={3}>3 intentos</Option>
                <Option value={5}>5 intentos</Option>
              </Select>
            </Form.Item>
          </Space>
        </Card>

        {/* Configuración de Notificaciones */}
        <Card
          title={
            <Space>
              <BellOutlined />
              <span>Notificaciones</span>
            </Space>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Form.Item
              name="notifications"
              label="Notificaciones del navegador"
              valuePropName="checked"
              extra="Recibe notificaciones cuando se complete el procesamiento"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="emailNotifications"
              label="Notificaciones por email"
              valuePropName="checked"
              extra="Recibe resúmenes por email de tu actividad"
            >
              <Switch />
            </Form.Item>
          </Space>
        </Card>

        {/* Botones de acción */}
        <Card>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
            >
              Guardar Configuración
            </Button>
            <Button onClick={() => form.resetFields()} size="large">
              Restablecer
            </Button>
          </Space>
        </Card>
      </Space>
    </Form>
  );
};

export default Configuration;
